/**
 * User overrides of an uploaded MCE mapping. An edit changes the normalized
 * MappingOverrides document, and a build in the browser checks the result before
 * it goes to `PUT /adapters/:id/cdo`, where the server rebuilds it again.
 */
import * as flatbuffers from "flatbuffers";
import type { DeviceType } from "../../flatbuffers/types/ln2MessageOptions";
import { ActiveWhen } from "../../flatbuffers/generated/mce/active-when";
import { ArgSource } from "../../flatbuffers/generated/mce/arg-source";
import { ByteOrder } from "../../flatbuffers/generated/mce/byte-order";
import { CommandAddress } from "../../flatbuffers/generated/mce/command-address";
import { CommandArgT } from "../../flatbuffers/generated/mce/command-arg";
import { DeclaredArgT } from "../../flatbuffers/generated/mce/declared-arg";
import { DeclaredCommandT } from "../../flatbuffers/generated/mce/declared-command";
import { FieldKind } from "../../flatbuffers/generated/mce/field-kind";
import { CommandRuleT } from "../../flatbuffers/generated/mce/command-rule";
import { CommandTargetT } from "../../flatbuffers/generated/mce/command-target";
import { ControlOverrideT } from "../../flatbuffers/generated/mce/control-override";
import { ControlRowT } from "../../flatbuffers/generated/mce/control-row";
import { EntryOverrideT } from "../../flatbuffers/generated/mce/entry-override";
import { FlagRuleT } from "../../flatbuffers/generated/mce/flag-rule";
import { IgnoreReason } from "../../flatbuffers/generated/mce/ignore-reason";
import { IgnoreRuleT } from "../../flatbuffers/generated/mce/ignore-rule";
import { KeyT } from "../../flatbuffers/generated/mce/key";
import {
  MappingOverrides,
  MappingOverridesT,
} from "../../flatbuffers/generated/mce/mapping-overrides";
import { OutRule } from "../../flatbuffers/generated/mce/out-rule";
import { ProductType } from "../../flatbuffers/generated/mce/product-type";
import { Rule } from "../../flatbuffers/generated/mce/rule";
import { TemplateT } from "../../flatbuffers/generated/mce/template";

import { asString } from "./messages";

/** An edit of one IDL entry: not sent to LN2, or a flag on one LN2 member. */
export type MceEntryEdit =
  | { kind: "ignore"; reason: IgnoreReason }
  | { kind: "flag"; template: TemplateT; activeWhen: ActiveWhen };

/** The names an entry edit can use, from the generated enums. */
export function enumOptions(generated: Record<string, string | number>) {
  return Object.entries(generated)
    .filter(([, value]) => typeof value === "number")
    .map(([label, value]) => ({ label, value: value as number }));
}

/** The options of the entry editor, kept here so the lists come from one place. */
export const IGNORE_REASON_OPTIONS = enumOptions(IgnoreReason as never);
export const ACTIVE_WHEN_OPTIONS = enumOptions(ActiveWhen as never);

/** The device form of a product's messages: M3000 is the escalator controller. */
export function deviceOfProduct(product: ProductType): DeviceType {
  return product === ProductType.M3000 ? "escalator" : "elevator";
}

/** An overrides blob, once inflated, is a size-prefixed MCEO buffer. */
export function unpackOverrides(inflated: Uint8Array): MappingOverridesT {
  const buffer = new flatbuffers.ByteBuffer(inflated);
  return MappingOverrides.getSizePrefixedRootAsMappingOverrides(buffer).unpack();
}

function sameKey(a: KeyT | null, b: KeyT): boolean {
  return (
    a !== null &&
    a.iface === b.iface &&
    a.category === b.category &&
    a.id === b.id &&
    a.subId === b.subId
  );
}

/** The document with one entry's override replaced, or removed when the edit is null. */
export function withEntryOverride(
  overrides: MappingOverridesT,
  key: KeyT,
  edit: MceEntryEdit | null,
): MappingOverridesT {
  const next = new MappingOverridesT(
    [...overrides.templates],
    overrides.entries.filter((entry) => !sameKey(entry.key, key)),
    overrides.events,
    overrides.controls,
  );
  if (!edit) return next;
  const override = new EntryOverrideT(key);
  if (edit.kind === "ignore") {
    override.ruleType = Rule.IgnoreRule;
    override.rule = new IgnoreRuleT(edit.reason);
  } else {
    next.templates.push(edit.template);
    override.ruleType = Rule.FlagRule;
    override.rule = new FlagRuleT(next.templates.length - 1, edit.activeWhen);
  }
  next.entries.push(override);
  return next;
}

/** An edit of one control row: stop sending it, or send one IDL command instead. */
export type MceControlEdit =
  | { kind: "ignore"; reason: IgnoreReason }
  | { kind: "commands"; commands: CommandTargetT[]; oneShot: boolean };

/**
 * The document with one control's override replaced, or removed when the edit
 * is null. A control is matched by its template's message, so the row's own
 * template goes in the document's list.
 */
export function withControlOverride(
  overrides: MappingOverridesT,
  row: ControlRowT,
  template: TemplateT,
  edit: MceControlEdit | null,
): MappingOverridesT {
  const packed = (one: TemplateT) => {
    const builder = new flatbuffers.Builder(128);
    builder.finish(one.pack(builder));
    return builder.asUint8Array().join(",");
  };
  const identity = packed(template);
  const next = new MappingOverridesT(
    [...overrides.templates],
    overrides.entries,
    overrides.events,
    overrides.controls.filter((control) => {
      const known = overrides.templates[control.match];
      return !known || packed(known) !== identity;
    }),
  );
  if (!edit) return next;
  let match = next.templates.findIndex((known) => packed(known) === identity);
  if (match < 0) {
    next.templates.push(template);
    match = next.templates.length - 1;
  }
  const override = new ControlOverrideT(match);
  if (edit.kind === "ignore") {
    override.ruleType = OutRule.IgnoreRule;
    override.rule = new IgnoreRuleT(edit.reason);
  } else {
    override.ruleType = OutRule.CommandRule;
    override.rule = new CommandRuleT(edit.commands, edit.oneShot);
  }
  next.controls.push(override);
  return next;
}

/** What the command editor can choose an argument's value from. */
export const ARG_SOURCE_OPTIONS = enumOptions(ArgSource as never);
/** Who a command addresses. Unspecified is not a choice. */
export const COMMAND_ADDRESS_OPTIONS = enumOptions(CommandAddress as never).filter(
  (option) => option.value !== CommandAddress.Unspecified,
);

/** One argument as the editor holds it. */
export interface MceArgDraft {
  name: string;
  typeName: string;
  width: number;
  kind: FieldKind;
  kindName: string;
  isBool: boolean;
  enumMembers: { label: string; value: number }[];
  source: ArgSource | null;
  constant: number | null;
  offset: bigint;
  map: bigint[];
}

/** One command as the editor holds it. */
export interface MceCommandDraft {
  commandKey: string;
  key: KeyT;
  address: CommandAddress | null;
  args: MceArgDraft[];
}

/** The identity of a command in the editor's lists. */
export function commandKeyOf(key: KeyT | null): string {
  return key ? `${key.iface}-${key.category}-${key.id}-${key.subId}` : "";
}

function argDraft(declared: DeclaredArgT, current: CommandArgT | undefined): MceArgDraft {
  return {
    name: asString(declared.name),
    typeName: asString(declared.typeName),
    width: declared.width,
    kind: declared.kind,
    kindName: FieldKind[declared.kind] ?? "",
    isBool: declared.kind === FieldKind.Bool,
    enumMembers: declared.enumMembers.map((member) => ({
      label: asString(member.name),
      value: Number(member.value),
    })),
    source: current?.source ?? null,
    constant: current ? Number(current.constant) : null,
    offset: current?.offset ?? BigInt(0),
    map: current?.map ?? [],
  };
}

/**
 * A declared command in the editor, its arguments filled from a current target
 * when it has one. A listing flattens structs into dotted names while a table's
 * rows use the field's own name, so arguments pair by their name's last part.
 */
export function commandDraft(
  declared: DeclaredCommandT,
  current: CommandTargetT | null = null,
): MceCommandDraft | null {
  if (!declared.key) return null;
  const lastPart = (name: string | Uint8Array | null) => asString(name).split(".").pop();
  return {
    commandKey: commandKeyOf(declared.key),
    key: declared.key,
    address: current && current.address !== CommandAddress.Unspecified ? current.address : null,
    args: declared.args.map((arg) =>
      argDraft(
        arg,
        current?.args.find((one) => lastPart(one.name) === lastPart(arg.name)),
      ),
    ),
  };
}

/** Every choice is made: a source for each argument, a value for each Constant, and a target. */
export function isDraftComplete(draft: MceCommandDraft): boolean {
  return (
    draft.address !== null &&
    draft.args.every(
      (arg) => arg.source !== null && (arg.source !== ArgSource.Constant || arg.constant !== null),
    )
  );
}

/** Compares the drafts' choices, to tell whether a person changed anything. */
export function draftsIdentity(drafts: MceCommandDraft[]): string {
  return JSON.stringify(
    drafts.map((draft) => [
      draft.commandKey,
      draft.address,
      draft.args.map((arg) => [arg.name, arg.source, arg.constant]),
    ]),
  );
}

/** Whether an argument takes its value from the editor. */
export const argNeedsConstant = (arg: MceArgDraft): boolean => arg.source === ArgSource.Constant;

/** The drafts as command targets. Width and kind come from the signature, never from a person. */
export function draftsToTargets(drafts: MceCommandDraft[], byteOrder: ByteOrder): CommandTargetT[] {
  return drafts.map(
    (draft) =>
      new CommandTargetT(
        draft.key,
        draft.address ?? CommandAddress.Unspecified,
        byteOrder,
        draft.args.map(
          (arg) =>
            new CommandArgT(
              arg.name,
              arg.source ?? ArgSource.Constant,
              arg.width,
              arg.kind,
              BigInt(arg.constant ?? 0),
              arg.offset,
              arg.map,
            ),
        ),
      ),
  );
}

/** The drafts of a control row, for each of its commands the IDL still declares. */
export function draftsOfRow(row: ControlRowT, declared: DeclaredCommandT[]): MceCommandDraft[] {
  if (!(row.rule instanceof CommandRuleT)) return [];
  return row.rule.commands.flatMap((current) => {
    const known = declared.find((one) => commandKeyOf(one.key) === commandKeyOf(current.key));
    const draft = known ? commandDraft(known, current) : null;
    return draft ? [draft] : [];
  });
}

/** The byte order a row's commands use, which a retarget keeps. MCE is little-endian. */
export function byteOrderOfRow(row: ControlRowT): ByteOrder {
  const current = row.rule instanceof CommandRuleT ? row.rule.commands[0]?.byteOrder : null;
  return current ?? ByteOrder.Little;
}

/** Whether a control row sends its commands once per activation. */
export function isOneShot(row: ControlRowT): boolean {
  return row.rule instanceof CommandRuleT && row.rule.oneShot;
}
